import { useSuspenseQuery } from "@apollo/client";
import { BreadcrumbArray, DataTable, PrimaryFullScreenSpinner } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { EburyBeneficiaryRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/ebury_/$clientId/beneficiaries")({
  head: () => opsHead("Ebury Client Beneficiaries"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const beneficiariesDocument = graphql(`
  query AdminEburyClientBeneficiaries($id: ID!) {
    eburyClient(id: $id) {
      id
      clientName
      beneficiaries {
        ...EburyBeneficiaryRow
      }
    }
  }
`);

type BeneficiaryRow = EburyBeneficiaryRowFragment;

const columns: ColumnDef<BeneficiaryRow>[] = [
  {
    accessorKey: "id",
    header: "Beneficiary ID",
    enableSorting: false,
  },
  {
    accessorKey: "name",
    header: "Name",
    enableSorting: false,
  },
  {
    accessorKey: "active",
    header: "Active",
    cell: ({ row }) => (row.original.active ? "Yes" : "No"),
    enableSorting: false,
  },
  {
    accessorKey: "amlStatus",
    header: "AML Status",
    cell: ({ row }) => row.original.amlStatus ?? "-",
    enableSorting: false,
  },
  {
    id: "address",
    header: "Address",
    cell: ({ row }) => {
      const { addressLine1, city, postCode, countryCode } = row.original;
      return (
        [addressLine1, city, postCode, countryCode]
          .filter(Boolean)
          .join(", ") || "-"
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "emailAddresses",
    header: "Emails",
    cell: ({ row }) => row.original.emailAddresses?.join(", ") ?? "-",
    enableSorting: false,
  },
];

function View() {
  const { clientId } = Route.useParams();
  const [selected, setSelected] = useState<BeneficiaryRow | null>(null);

  const { data } = useSuspenseQuery(beneficiariesDocument, {
    variables: { id: clientId },
  });

  const client = data.eburyClient;
  const beneficiaries = client?.beneficiaries ?? [];

  return (
    <AdminPageContainer testId="admin-ebury-client-beneficiaries">
      <BreadcrumbArray
        stack={[
          { to: "/ebury", title: "Ebury Clients" },
          { title: client?.clientName ?? clientId },
        ]}
      />
      <AdminPageHeader
        title={`${client?.clientName ?? clientId} — Beneficiaries`}
        subtitle={clientId}
      />
      <div className="flex min-h-0 gap-4">
        <div className="min-w-0 flex-1">
          <DataTable
            data={beneficiaries}
            columns={columns}
            activeRowId={selected?.id}
            onRowClick={(row) => {
              setSelected(
                row.original.id === selected?.id ? null : row.original,
              );
            }}
            className="overflow-hidden border"
          />
        </div>
        {selected && (
          <BeneficiaryDetailPane
            beneficiary={selected}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </AdminPageContainer>
  );
}

function BeneficiaryDetailPane({
  beneficiary,
  onClose,
}: {
  beneficiary: BeneficiaryRow;
  onClose: () => void;
}) {
  return (
    <div className="w-[480px] flex-shrink-0 overflow-auto border-l px-6 py-2">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold text-lg">Beneficiary Detail</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 hover:bg-secondary"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="text-sm">
        <DetailRow label="ID" value={beneficiary.id} />
        <DetailRow label="Short Ref" value={beneficiary.shortRef} />
        <DetailRow label="Name" value={beneficiary.name} />
        <DetailRow label="Active" value={beneficiary.active ? "Yes" : "No"} />
        <DetailRow label="AML Status" value={beneficiary.amlStatus ?? "-"} />
        <DetailRow label="Created" value={beneficiary.created} />
        <DetailRow label="Reference" value={beneficiary.beneficiaryReference} />
        <DetailRow label="Country" value={beneficiary.countryCode} />
        <DetailRow label="Address" value={beneficiary.addressLine1 ?? "-"} />
        <DetailRow
          label="Building No"
          value={beneficiary.buildingNumber ?? "-"}
        />
        <DetailRow label="Floor" value={beneficiary.floor ?? "-"} />
        <DetailRow
          label="Apt / Office"
          value={beneficiary.apartmentOfficeNumber ?? "-"}
        />
        <DetailRow label="Street" value={beneficiary.streetName ?? "-"} />
        <DetailRow label="City" value={beneficiary.city ?? "-"} />
        <DetailRow label="Post Code" value={beneficiary.postCode} />
        <DetailRow
          label="State / Region"
          value={beneficiary.stateRegion ?? "-"}
        />
        <DetailRow
          label="Emails"
          value={beneficiary.emailAddresses.join(", ") || "-"}
        />
        <DetailRow
          label="Email Notif."
          value={beneficiary.emailNotification ? "Yes" : "No"}
        />

        {beneficiary.bankAccounts.length > 0 && (
          <DetailRow label="Bank Accounts">
            {beneficiary.bankAccounts.map((account) => (
              <div key={account.accountId} className="mb-3 rounded border p-3">
                <DetailSubRow label="Account ID" value={account.accountId} />
                <DetailSubRow label="Bank Name" value={account.bankName} />
                <DetailSubRow
                  label="Currency"
                  value={account.bankCurrencyCode}
                />
                <DetailSubRow label="Country" value={account.bankCountryCode} />
                <DetailSubRow
                  label="Account No."
                  value={account.accountNumber ?? "-"}
                />
                <DetailSubRow label="IBAN" value={account.iban ?? "-"} />
                <DetailSubRow
                  label="Swift Code"
                  value={account.swiftCode ?? "-"}
                />
                <DetailSubRow
                  label="Bank Identifier"
                  value={account.bankIdentifier ?? "-"}
                />
                <DetailSubRow
                  label="Bank Address"
                  value={account.bankAddressLine1}
                />
                <DetailSubRow
                  label="Correspondent Acct"
                  value={account.correspondentAccount ?? "-"}
                />
                <DetailSubRow
                  label="Correspondent Swift"
                  value={account.correspondentSwiftCode ?? "-"}
                />
                <DetailSubRow
                  label="Ref Information"
                  value={account.referenceInformation ?? "-"}
                />
              </div>
            ))}
          </DetailRow>
        )}
      </div>
    </div>
  );
}
